<template>
  <div>
    <b-card no-body>
      <div class="card-header">
        <b-card-title>{{ $t('settings.city.list') }}</b-card-title>
        <div class="d-flex align-items-center">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            :to="{ name: 'settings-cities-add' }"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            <span class="align-middle">{{ $t('settings.city.add') }}</span>
          </b-button>
        </div>
      </div>

      <div class="card-body">
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group>
              <b-form-input
                v-model="filters.search"
                :placeholder="$t('common.search')"
                @input="fetchCities"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <v-select
                v-model="filters.governorate_id"
                :options="governoratesDropdown"
                :reduce="val => val.id"
                label="name_ar"
                :placeholder="$t('settings.city.governorate_id')"
                @input="fetchCities"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <v-select
                v-model="filters.is_active"
                :options="statusOptions"
                :reduce="val => val.value"
                :placeholder="$t('common.status')"
                @input="fetchCities"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="2"
          >
            <b-button
              variant="outline-secondary"
              block
              @click="clearFilters"
            >
              {{ $t('common.clear') }}
            </b-button>
          </b-col>
        </b-row>

        <b-table
          :items="cities"
          :fields="fields"
          :busy="loading"
          responsive
          show-empty
          :empty-text="$t('common.no_data')"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle mr-1" />
              <strong>{{ $t('common.loading') }}</strong>
            </div>
          </template>

          <template #cell(governorate)="data">
            {{ data.item.governorate ? data.item.governorate.name_ar : '-' }}
          </template>

          <template #cell(is_active)="data">
            <b-badge :variant="data.item.is_active ? 'success' : 'danger'">
              {{ data.item.is_active ? $t('common.active') : $t('common.inactive') }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item :to="{ name: 'settings-cities-view', params: { id: data.item.id } }">
                <feather-icon icon="EyeIcon" />
                <span class="align-middle ml-50">{{ $t('common.view') }}</span>
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'settings-cities-edit', params: { id: data.item.id } }">
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">{{ $t('common.edit') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click="toggleStatus(data.item)">
                <feather-icon icon="ToggleLeftIcon" />
                <span class="align-middle ml-50">{{ $t('common.toggle_status') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click="deleteCity(data.item)">
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">{{ $t('common.delete') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="center"
          @change="handlePageChange"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BCardTitle,
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BSpinner,
    BPagination,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      filters: {
        search: '',
        governorate_id: null,
        is_active: null,
      },
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      statusOptions: [
        { label: this.$t('common.all'), value: null },
        { label: this.$t('common.active'), value: true },
        { label: this.$t('common.inactive'), value: false },
      ],
      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'name_ar', label: this.$t('settings.city.name_ar'), sortable: true },
        { key: 'name_en', label: this.$t('settings.city.name_en'), sortable: true },
        { key: 'governorate', label: this.$t('settings.city.governorate_id'), sortable: true },
        { key: 'districts_count', label: this.$t('settings.city.districts_count'), sortable: true },
        { key: 'is_active', label: this.$t('common.status'), sortable: true },
        { key: 'actions', label: this.$t('common.actions') },
      ],
    }
  },
  computed: {
    ...mapGetters({
      cities: 'cities/cities',
      loading: 'cities/load',
      governoratesDropdown: 'governorates/dropdown',
    }),
  },
  mounted() {
    this.fetchGovernoratesDropdown()
    this.fetchCities()
  },
  methods: {
    fetchGovernoratesDropdown() {
      this.$store.dispatch('governorates/dropdown')
    },
    fetchCities() {
      const params = {
        ...this.filters,
        page: this.currentPage,
        per_page: this.perPage,
      }
      this.$store.dispatch('cities/list', params)
        .then(response => {
          if (response.meta) {
            this.totalRows = response.meta.total
          }
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.error'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: (error.response && error.response.data && error.response.data.message) || error.message,
            },
          })
        })
    },
    clearFilters() {
      this.filters = {
        search: '',
        governorate_id: null,
        is_active: null,
      }
      this.fetchCities()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.fetchCities()
    },
    toggleStatus(city) {
      this.$swal({
        title: this.$t('common.confirm'),
        text: this.$t('settings.city.messages.toggle_status_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.yes'),
        cancelButtonText: this.$t('common.no'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store.dispatch('cities/toggleStatus', city.id)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('common.success'),
                  icon: 'CheckIcon',
                  variant: 'success',
                  text: this.$t('settings.city.messages.toggle_status_success'),
                },
              })
              this.fetchCities()
            })
            .catch(error => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('common.error'),
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                  text: error.response?.data?.message || error.message,
                },
              })
            })
        }
      })
    },
    deleteCity(city) {
      this.$swal({
        title: this.$t('common.confirm'),
        text: this.$t('settings.city.messages.delete_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.yes'),
        cancelButtonText: this.$t('common.no'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store.dispatch('cities/delete', city.id)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('common.success'),
                  icon: 'CheckIcon',
                  variant: 'success',
                  text: this.$t('settings.city.messages.delete_success'),
                },
              })
              this.fetchCities()
            })
            .catch(error => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('common.error'),
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                  text: error.response?.data?.message || error.message,
                },
              })
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/vue/libs/vue-select.scss';
</style>
